/* eslint-disable react/prop-types */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography,
  Table,
  Button,
  Tooltip,
  Space,
  Modal,
  message,
  Input,
  Form,
  Layout,
  Row,
  Col,
  DatePicker,
  ConfigProvider,
} from 'antd';
import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  ShareAltOutlined,
  CloseOutlined,
  ArrowLeftOutlined,
  SearchOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  FileImageOutlined,
  CopyOutlined,
  UploadOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import axiosConfig from '../../services/axiosConfig';
import crceTechnicalHeatProfileAxios from '../../redux/heatProfile/crceTechnicalHeatProfileAxiosConfig';

/** Import modal list: CRCE technical API (`REACT_APP_AWS_CRCE_TECHNICAL_HEAT_PROFILE`). */
const TECHNICAL_HEAT_PROFILE_LIST_PATH = 'technical-heat-profile';
/** Add selected profiles: PIH main API (`REACT_APP_BASE_URL` via services/axiosConfig). */
const PIH_PROJECT_HEAT_PROFILE_BULK_UPLOAD_PATH = '/project-heat-profile/bulk-upload';

const { Search } = Input;
const { Title, Text } = Typography;
const { Header, Content } = Layout;

const EmailReportForm = ({ record, onSend, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      await onSend({
        emails: values.emails.split(',').map((email) => email.trim()),
        subject: values.subject,
        message: values.message,
      });
    } catch (error) {
      console.error('Error sending email:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={{
        subject: `Heat Profile Report - ${record?.reportNumber || 'N/A'}`,
        message: 'Please find attached the heat profile report for review and approval.',
      }}
    >
      <Form.Item
        label="Recipient Emails (comma separated)"
        name="emails"
        rules={[
          { required: true, message: 'Please enter at least one email address' },
          {
            validator: (_, value) => {
              if (!value) return Promise.resolve();
              const emails = value.split(',').map((email) => email.trim());
              const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
              const invalidEmails = emails.filter((email) => !emailRegex.test(email));
              return invalidEmails.length > 0
                ? Promise.reject(new Error(`Invalid email(s): ${invalidEmails.join(', ')}`))
                : Promise.resolve();
            },
          },
        ]}
      >
        <Input.TextArea
          placeholder="engineer@pipeline-technique.com, manager@pipeline-technique.com"
          rows={2}
        />
      </Form.Item>

      <Form.Item
        label="Subject"
        name="subject"
        rules={[{ required: true, message: 'Please enter email subject' }]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        label="Message"
        name="message"
        rules={[{ required: true, message: 'Please enter email message' }]}
      >
        <Input.TextArea rows={4} />
      </Form.Item>

      <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
        <Space>
          <Button onClick={onCancel}>Cancel</Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            Send Email
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
};

const TECHNICAL_HEAT_PROFILE_IMPORT_PAGE = { pageNumber: 1, pageSize: 1000 };

const getImportHeatProfileRowKey = (row) =>
  row.id != null && row.id !== '' ? row.id : `${row.reportNumber}-${row.projectNumber}`;

function normalizeReportNumberForDedup(value) {
  if (value == null || value === '') return '';
  return String(value).trim().toLowerCase();
}

/** Matches ProjectHeatProfile: formData.reportAuthor || name || 'Project User' (no form here → logged-in user). */
function getProjectHeatProfileReportAuthor() {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return 'Project User';
    const user = JSON.parse(raw);
    if (user?.name) return user.name;
    const first = user['custom:first_name'] || '';
    const last = user['custom:last_name'] || '';
    const combined = `${first} ${last}`.trim();
    return combined || 'Project User';
  } catch {
    return 'Project User';
  }
}

/** API sends `createdAt` as calendar date string, e.g. "2024-09-18". */
const API_DATE_YMD = /^\d{4}-\d{2}-\d{2}$/;

/** Resolve created timestamp from common API shapes, nesting, and unknown *created* keys. */
function getRowCreatedAtForFilter(row) {
  if (!row || typeof row !== 'object') return undefined;
  const directKeys = [
    'createdAt',
    'CreatedAt',
    'created_at',
    'creationDate',
    'CreationDate',
    'creation_date',
    'dateCreated',
    'DateCreated',
    'created_time',
    'Created_Time',
    'insertDate',
    'InsertDate',
    'created',
    'Created',
  ];
  for (let i = 0; i < directKeys.length; i += 1) {
    const v = row[directKeys[i]];
    if (v != null && v !== '') return v;
  }
  const nested = [row.meta, row.data, row.attributes, row.properties, row.heatProfile];
  for (let i = 0; i < nested.length; i += 1) {
    const obj = nested[i];
    if (!obj || typeof obj !== 'object') continue;
    const nk = ['createdAt', 'CreatedAt', 'created_at', 'creationDate', 'CreationDate'];
    for (let j = 0; j < nk.length; j += 1) {
      const v = obj[nk[j]];
      if (v != null && v !== '') return v;
    }
  }
  const skipKey = /^(trailDate|trialDate|updatedAt|UpdatedAt|modifiedAt|ModifiedAt)$/i;
  const keys = Object.keys(row);
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    if (skipKey.test(key)) continue;
    if (!/created|creation|insert|timestamp/i.test(key)) continue;
    const v = row[key];
    if (v != null && v !== '' && typeof v !== 'object') return v;
  }
  return undefined;
}

function yyyyMmDdFromApiDate(raw) {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const ms = raw > 1e12 ? raw : raw * 1000;
    const m = moment(ms);
    return m.isValid() ? m.format('YYYY-MM-DD') : null;
  }
  const s = String(raw).trim();
  if (API_DATE_YMD.test(s)) return s;
  const head = s.slice(0, 10);
  if (API_DATE_YMD.test(head)) return head;
  let m = moment(s, 'YYYY-MM-DD', true);
  if (m.isValid()) return m.format('YYYY-MM-DD');
  m = moment(s);
  return m.isValid() ? m.format('YYYY-MM-DD') : null;
}

/** Filter state `createdDate` is YYYY-MM-DD; compare to row created field. */
function rowMatchesCreatedDateFilter(row, filterYyyyMmDd) {
  const filterNorm = filterYyyyMmDd != null ? String(filterYyyyMmDd).trim() : '';
  if (!filterNorm) return true;
  const ymd = yyyyMmDdFromApiDate(getRowCreatedAtForFilter(row));
  return ymd === filterNorm;
}

/** Table cells: "2025 May 07" (year, short month name, zero-padded day). */
const TABLE_DATE_DISPLAY_FORMAT = 'YYYY MMM DD';

function formatTableDateDisplay(raw) {
  if (raw == null || raw === '') return 'N/A';
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const ms = raw > 1e12 ? raw : raw * 1000;
    const m = moment(ms);
    return m.isValid() ? m.format(TABLE_DATE_DISPLAY_FORMAT) : 'N/A';
  }
  const s = String(raw).trim();
  const head = s.slice(0, 10);
  if (API_DATE_YMD.test(head)) {
    const md = moment(head, 'YYYY-MM-DD', true);
    return md.isValid() ? md.format(TABLE_DATE_DISPLAY_FORMAT) : 'N/A';
  }
  const m = moment(s);
  return m.isValid() ? m.format(TABLE_DATE_DISPLAY_FORMAT) : 'N/A';
}

/** CRCE list may return camelCase or PascalCase per row. */
function normalizeCrceImportRow(row) {
  if (!row || typeof row !== 'object') return row;
  return {
    ...row,
    id: row.id ?? row.Id,
    reportNumber: row.reportNumber ?? row.ReportNumber,
    projectName: row.projectName ?? row.ProjectName,
    projectNumber: row.projectNumber ?? row.ProjectNumber,
    createdAt: getRowCreatedAtForFilter(row),
  };
}

const ProjectHeatProfileList = (props) => {
  const { selectedProject, selectedSOW, onEdit, onCreateNew, setIsHeatProfileModalVisible } = props;

  // Internal back handler: closes the list modal if setVisible is provided
  const onBack = () => {
    setIsHeatProfileModalVisible(false);
  };

  // State variables
  const [projectHeatProfiles, setProjectHeatProfiles] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [searchFilters, setSearchFilters] = useState({
    reportNumber: '',
    projectName: '',
    projectNumber: '',
    reportAuthor: '',
    material: '',
    trialDate: '',
    createdDate: '',
  });

  // Modal states
  const [isReportModalVisible, setIsReportModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [isImportHeatProfileModalVisible, setIsImportHeatProfileModalVisible] = useState(false);
  const [selectedImportRowKeys, setSelectedImportRowKeys] = useState([]);
  const [importBulkSubmitLoading, setImportBulkSubmitLoading] = useState(false);
  const [projectHeatProfilesRefreshToken, setProjectHeatProfilesRefreshToken] = useState(0);
  const [crceTechnicalImportList, setCrceTechnicalImportList] = useState([]);
  const [crceTechnicalImportListLoading, setCrceTechnicalImportListLoading] = useState(false);
  const [importSearchFilters, setImportSearchFilters] = useState({
    reportNumber: '',
    projectName: '',
    projectNumber: '',
    createdDate: '',
  });
  const [importTablePagination, setImportTablePagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const importHeatProfileTableRows = useMemo(() => {
    const raw = crceTechnicalImportList;
    let rows = [];
    if (Array.isArray(raw)) rows = raw;
    else if (raw && typeof raw === 'object' && Array.isArray(raw.records)) rows = raw.records;
    else return [];

    const existingReportNumbers = new Set(
      projectHeatProfiles
        .map((p) => normalizeReportNumberForDedup(p.reportNumber))
        .filter((k) => k !== '')
    );

    return rows
      .filter((row) => {
        const r = normalizeCrceImportRow(row);
        const key = normalizeReportNumberForDedup(r.reportNumber);
        if (!key) return true;
        return !existingReportNumbers.has(key);
      })
      .map(normalizeCrceImportRow);
  }, [crceTechnicalImportList, projectHeatProfiles]);

  const handleOpenImportHeatProfileModal = async () => {
    setImportSearchFilters({
      reportNumber: '',
      projectName: '',
      projectNumber: '',
      createdDate: '',
    });
    setImportTablePagination({ current: 1, pageSize: 10 });
    setSelectedImportRowKeys([]);
    setIsImportHeatProfileModalVisible(true);
    setCrceTechnicalImportListLoading(true);
    try {
      const res = await crceTechnicalHeatProfileAxios.get(TECHNICAL_HEAT_PROFILE_LIST_PATH, {
        params: {
          pageNumber: TECHNICAL_HEAT_PROFILE_IMPORT_PAGE.pageNumber,
          pageSize: TECHNICAL_HEAT_PROFILE_IMPORT_PAGE.pageSize,
        },
      });
      const raw = res?.data?.data ?? res?.data;
      let rows = [];
      if (Array.isArray(raw)) {
        rows = raw;
      } else if (raw && typeof raw === 'object' && Array.isArray(raw.records)) {
        rows = raw.records;
      }
      setCrceTechnicalImportList(rows);
    } catch (error) {
      console.error('CRCE technical heat profile list fetch failed:', error);
      message.error(
        error?.message ||
          error?.response?.data?.message ||
          'Failed to load technical heat profiles for import.'
      );
      setCrceTechnicalImportList([]);
    } finally {
      setCrceTechnicalImportListLoading(false);
    }
  };

  const handleCloseImportHeatProfileModal = () => {
    setIsImportHeatProfileModalVisible(false);
    setSelectedImportRowKeys([]);
  };

  const handleAddHeatProfileFromImportModal = async () => {
    const selectedRows = importHeatProfileTableRows.filter((row) =>
      selectedImportRowKeys.some((k) => String(k) === String(getImportHeatProfileRowKey(row)))
    );

    if (selectedRows.length === 0) {
      message.warning('Select at least one heat profile to add.');
      return;
    }
    if (!selectedProject?.id) {
      message.error('Project is not selected.');
      return;
    }

    setImportBulkSubmitLoading(true);
    try {
      const reportAuthor = getProjectHeatProfileReportAuthor();
      const projectId = selectedProject.id;
      const payload = {
        data: selectedRows.map((row) => ({
          ...row,
          projectId,
          reportAuthor,
        })),
      };

      const response = await axiosConfig.post(PIH_PROJECT_HEAT_PROFILE_BULK_UPLOAD_PATH, payload);

      if (response.status === 200 || response.status === 201) {
        message.success(
          response.data?.message || `${selectedRows.length} heat profile(s) submitted successfully.`
        );
        setProjectHeatProfilesRefreshToken((t) => t + 1);
        handleCloseImportHeatProfileModal();
      }
    } catch (error) {
      console.error('Bulk technical heat profile import failed:', error);
      const description =
        error?.message ||
        error?.error ||
        error?.response?.data?.message ||
        'Failed to add heat profiles. Please try again.';
      message.error(description);
    } finally {
      setImportBulkSubmitLoading(false);
    }
  };


  // Fetch project heat profiles
  useEffect(() => {
    if (selectedProject?.id) {
      const fetchProjectHeatProfiles = async () => {
        setIsLoadingData(true);
        try {
          console.log('Fetching project heat profiles for project:', selectedProject.id);
          const response = await axiosConfig.get('/project-heat-profile', {
            params: {
              projectId: selectedProject.id,
            },
          });

          console.log('API Response:', response);

          if (response.status === 200) {
            const data = response.data;
            let processedData = [];

            if (Array.isArray(data)) {
              processedData = data;
            } else if (data && typeof data === 'object' && Array.isArray(data.data)) {
              processedData = data.data;
            } else if (data && typeof data === 'object' && Array.isArray(data.records)) {
              processedData = data.records;
            } else {
              console.warn('API returned non-array data:', data);
              processedData = [];
            }

            // Transform the data
            const transformedData = processedData.map((item) => ({
              ...item,
              projectName: item.project?.projectName || item.projectName || 'N/A',
              projectNumber: item.project?.projectId || item.projectNumber || 'N/A',
            }));

            console.log('Setting transformed project heat profiles:', transformedData);
            setProjectHeatProfiles(transformedData);
          } else {
            console.warn('API returned non-200 status:', response.status);
            setProjectHeatProfiles([]);
          }
        } catch (error) {
          console.error('Error fetching project heat profiles:', error);
          message.error('Failed to fetch project heat profiles');
          setProjectHeatProfiles([]);
        } finally {
          setIsLoadingData(false);
        }
      };

      fetchProjectHeatProfiles();
    }
  }, [selectedProject, selectedSOW, projectHeatProfilesRefreshToken]);

  // Filter data based on search filters
  const filteredData = projectHeatProfiles.filter((item) => {
    return Object.keys(searchFilters).every((key) => {
      if (!searchFilters[key]) return true;

      if (key === 'createdDate') {
        return rowMatchesCreatedDateFilter(item, searchFilters.createdDate);
      }

      if (key === 'trialDate') {
        const raw = item.trailDate ?? item.trialDate;
        if (raw == null || raw === '') return false;
        const m = moment(raw);
        if (!m.isValid()) return false;
        return m.format('YYYY-MM-DD') === searchFilters.trialDate;
      }

      const value = item[key];
      if (typeof value === 'string') {
        return value.toLowerCase().includes(searchFilters[key].toLowerCase());
      }
      if (value !== undefined && value !== null) {
        return String(value).toLowerCase().includes(searchFilters[key].toLowerCase());
      }
      return false;
    });
  });

  // Handle search filter changes
  const normalizeCreatedDateFilterValue = (value) => {
    if (value == null || value === '') return '';
    const s = String(value).trim();
    if (API_DATE_YMD.test(s)) return s;
    const head = s.slice(0, 10);
    return API_DATE_YMD.test(head) ? head : s;
  };

  const handleSearchChange = (field, value) => {
    setSearchFilters((prev) => ({
      ...prev,
      [field]: field === 'createdDate' ? normalizeCreatedDateFilterValue(value) : value,
    }));
  };

  const handleImportSearchChange = (field, value) => {
    setImportSearchFilters((prev) => ({
      ...prev,
      [field]: field === 'createdDate' ? normalizeCreatedDateFilterValue(value) : value,
    }));
  };

  // Action handlers
  const handleView = (record) => {
    if (record.pdfReportDetails && record.pdfReportDetails.s3Url) {
      console.log('record.pdfReportDetails.s3Url:', record.pdfReportDetails.s3Url);
      window.open(record.pdfReportDetails.s3Url, '_blank');
    } else {
      message.warning('PDF report not available for this record.');
    }
  };

  const handleViewText = (record) => {
    if (record.textFileDetails && record.textFileDetails.s3Url) {
      window.open(record.textFileDetails.s3Url, '_blank');
    } else {
      message.warning('Text file not available for this record.');
    }
  };

  const handleViewImage = (record) => {
    if (record.imageFileDetails && record.imageFileDetails.s3Url) {
      window.open(record.imageFileDetails.s3Url, '_blank');
    } else {
      message.warning('Image file not available for this record.');
    }
  };

  const handleEdit = (record) => {
    console.log('Edit record:', record);
    setIsEditLoading(true);

    // Show updating message
    message.loading('Loading heat profile for editing...', 1.5).then(() => {
      if (onEdit) {
        onEdit(record);
      } else {
        message.info('Edit functionality will be implemented');
      }
      setIsEditLoading(false);
    });
  };

  const handleShare = (record) => {
    setSelectedRecord(record);
    setIsReportModalVisible(true);
  };

  const handleCopy = (record) => {
    // Copy record details to clipboard
    const recordInfo = `Heat Profile Details:
Report Number: ${record.reportNumber || 'N/A'}
Project Name: ${record.projectName || 'N/A'}
Project Number: ${record.projectNumber || 'N/A'}
Report Author: ${record.reportAuthor || 'N/A'}
Material: ${record.material || 'N/A'}
Trial Date: ${record.trailDate ? moment(record.trailDate).format('YYYY-MM-DD') : 'N/A'}
Created Date: ${yyyyMmDdFromApiDate(getRowCreatedAtForFilter(record)) ?? 'N/A'}`;

    navigator.clipboard
      .writeText(recordInfo)
      .then(() => {
        message.success('Heat profile details copied to clipboard');
      })
      .catch(() => {
        message.error('Failed to copy to clipboard');
      });
  };

  const handleSendReport = async (emailData) => {
    try {
      console.log('Sending report for record:', selectedRecord);
      const requestBody = {
        id: selectedRecord.id,
        recipientEmails: emailData.emails,
        subject: emailData.subject || `Heat Profile Report - ${selectedRecord.reportNumber}`,
        message:
          emailData.message ||
          'Please find attached the heat profile report for review and approval.',
        includeAttachments: true,
      };

      const response = await axiosConfig.post('/project-heat-profile/report', requestBody, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 200) {
        message.success('Report sent successfully');
        setIsReportModalVisible(false);
        setSelectedRecord(null);
      }
    } catch (error) {
      console.error('Error sending report:', error);
      message.error('Failed to send report');
    }
  };

  const showDeleteModal = (record) => {
    setSelectedRecord(record);
    setIsDeleteModalVisible(true);
  };

  const handleDelete = async () => {
    setIsDeleteLoading(true);
    try {
      await axiosConfig.delete(`/project-heat-profile/${selectedRecord.id}`);
      message.success('Project Heat Profile deleted successfully');

      // Refresh the list by removing the deleted item from local state
      setProjectHeatProfiles((prev) => prev.filter((item) => item.id !== selectedRecord.id));
    } catch (error) {
      message.error('Failed to delete Project Heat Profile');
    }
    setIsDeleteLoading(false);
    setIsDeleteModalVisible(false);
    setSelectedRecord(null);
  };

  const cancelDelete = () => {
    setIsDeleteModalVisible(false);
    setSelectedRecord(null);
  };

  // Create search inputs for table header (no search icon)
  const createSearchInput = (field, placeholder) => (
    <div className="searchEquip">
      <Input
        placeholder={placeholder}
        value={searchFilters[field]}
        onChange={(e) => handleSearchChange(field, e.target.value)}
        allowClear
      />
    </div>
  );
  // Custom styles for search input to match provided design
  const style = document.createElement('style');
  style.innerHTML = `
.searchEquip .ant-input-affix-wrapper .ant-input {
    background: #0000 !important;
    border-radius: 0 !important;
}
.ant-input-affix-wrapper > .ant-input:not(textarea) {
    padding: 0 !important;
}
.ant-input:placeholder-shown {
    font-size: 14px !important;
    text-overflow: ellipsis;
}
.ant-input-affix-wrapper > .ant-input {
    border: none !important;
    font-size: inherit !important;
    outline: none !important;
}
`;
  if (typeof window !== 'undefined' && !document.getElementById('custom-search-equip-style')) {
    style.id = 'custom-search-equip-style';
    document.head.appendChild(style);
  }

  const importCheckboxStyleEl = document.createElement('style');
  importCheckboxStyleEl.id = 'import-heat-profile-checkbox-style';
  importCheckboxStyleEl.innerHTML = `
.import-heat-profile-modal .ant-checkbox:not(.ant-checkbox-checked) .ant-checkbox-inner {
  border-color: #ff4d4f !important;
}
.import-heat-profile-modal .ant-checkbox:not(.ant-checkbox-checked):hover .ant-checkbox-inner,
.import-heat-profile-modal .ant-checkbox:not(.ant-checkbox-checked) .ant-checkbox-input:focus + .ant-checkbox-inner {
  border-color: #ff7875 !important;
}
/* Neutral pagination: beats .pipeline-modal (red select) and .equipPaginationTable (#ef314c) */
.import-heat-profile-modal .import-heat-profile-import-table .ant-table-pagination.ant-pagination {
  padding: 16px;
  margin: 0;
  background: transparent;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}
.import-heat-profile-modal .ant-pagination {
  color: rgba(255, 255, 255, 0.85);
}
.import-heat-profile-modal .ant-pagination-total-text {
  color: rgba(255, 255, 255, 0.85);
}
.import-heat-profile-modal .ant-pagination-options .ant-select {
  margin-left: 2px;
}
.import-heat-profile-modal .ant-pagination .ant-select-single .ant-select-selector {
  background: #2d2d2d !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  border-color: rgba(255, 255, 255, 0.2) !important;
  border-radius: 0 !important;
  color: #fff !important;
  box-shadow: none !important;
  height: 24px !important;
  min-height: 24px !important;
  line-height: 22px !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  padding-left: 8px !important;
  padding-right: 22px !important;
}
.import-heat-profile-modal .ant-pagination .ant-select-single .ant-select-selection-item {
  line-height: 22px !important;
  height: 22px !important;
  min-height: 0 !important;
}
.import-heat-profile-modal .ant-pagination .ant-select .ant-select-selection-search-input {
  height: 22px !important;
  line-height: 22px !important;
}
.import-heat-profile-modal .ant-pagination .ant-select .ant-select-arrow {
  top: 50% !important;
  transform: translateY(-50%) !important;
  margin-top: 0 !important;
  right: 8px !important;
  color: rgba(255, 255, 255, 0.65) !important;
}
.import-heat-profile-modal .ant-pagination .ant-select:not(.ant-select-disabled):hover .ant-select-selector,
.import-heat-profile-modal .ant-pagination .ant-select.ant-select-open .ant-select-selector,
.import-heat-profile-modal .ant-pagination .ant-select-focused:not(.ant-select-disabled) .ant-select-selector {
  border-color: rgba(255, 255, 255, 0.28) !important;
  box-shadow: none !important;
}
.import-heat-profile-modal .ant-pagination .ant-select .ant-select-selection-item,
.import-heat-profile-modal .ant-pagination .ant-select .ant-select-selection-placeholder {
  color: #fff !important;
}
.import-heat-profile-modal .ant-pagination .ant-select .ant-select-arrow .anticon {
  color: rgba(255, 255, 255, 0.65) !important;
}
.import-heat-profile-modal .ant-pagination-item {
  border-radius: 0 !important;
}
.import-heat-profile-modal .ant-pagination-item a {
  color: rgba(255, 255, 255, 0.85) !important;
}
.import-heat-profile-modal .ant-pagination-item:hover,
.import-heat-profile-modal .ant-pagination-item-active,
.import-heat-profile-modal .ant-pagination-item-active:hover {
  border-color: rgba(255, 255, 255, 0.35) !important;
}
.import-heat-profile-modal .ant-pagination-item:hover a,
.import-heat-profile-modal .ant-pagination-item-active a {
  color: #fff !important;
}
.import-heat-profile-modal .ant-pagination-prev .ant-pagination-item-link,
.import-heat-profile-modal .ant-pagination-next .ant-pagination-item-link {
  background: #2d2d2d !important;
  border-color: rgba(255, 255, 255, 0.2) !important;
  border-radius: 0 !important;
  color: #fff !important;
}
.import-heat-profile-modal .ant-pagination-prev:hover .ant-pagination-item-link,
.import-heat-profile-modal .ant-pagination-next:hover .ant-pagination-item-link {
  border-color: rgba(255, 255, 255, 0.35) !important;
  color: #fff !important;
}
.import-heat-profile-modal .ant-pagination-jump-prev .ant-pagination-item-container .ant-pagination-item-link-icon,
.import-heat-profile-modal .ant-pagination-jump-next .ant-pagination-item-container .ant-pagination-item-link-icon {
  color: rgba(255, 255, 255, 0.65) !important;
}
.import-heat-profile-modal .ant-pagination-jump-prev,
.import-heat-profile-modal .ant-pagination-jump-next {
  color: rgba(255, 255, 255, 0.65);
}
.import-heat-profile-modal .ant-pagination-options-quick-jumper {
  color: rgba(255, 255, 255, 0.85);
}
.import-heat-profile-modal .ant-pagination-options-quick-jumper input {
  background: #2d2d2d !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  border-radius: 0 !important;
  color: #fff !important;
  height: 24px !important;
  line-height: 22px !important;
  padding: 0 8px !important;
}
.import-heat-profile-modal .ant-pagination-options-quick-jumper input:hover,
.import-heat-profile-modal .ant-pagination-options-quick-jumper input:focus {
  border-color: rgba(255, 255, 255, 0.28) !important;
  box-shadow: none !important;
}
.import-heat-profile-modal .ant-select-dropdown {
  background: #2d2d2d;
}
.import-heat-profile-modal .ant-select-item {
  color: #fff;
}
.import-heat-profile-modal .ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
  background: #1a1a1a;
  color: #fff;
}
.import-heat-profile-modal .ant-select-item-option-active:not(.ant-select-item-option-disabled) {
  background: #404040;
}
`;
  if (typeof window !== 'undefined' && !document.getElementById('import-heat-profile-checkbox-style')) {
    document.head.appendChild(importCheckboxStyleEl);
  }

  /**
   * DatePicker for table header (mousedown stopPropagation) or toolbar (no wrapper).
   * Import modal uses toolbar — table header DatePickers often fail to commit in antd Table.
   */
  const createHeaderDatePicker = (field, placeholder, filters, setField, options = {}) => {
    const { wrapForTableHeader = true } = options;
    const raw = filters[field];
    const valueMoment =
      raw && moment(raw, 'YYYY-MM-DD', true).isValid() ? moment(raw, 'YYYY-MM-DD') : null;
    const picker = (
      <DatePicker
        placeholder={placeholder}
        value={valueMoment}
        onChange={(date) => setField(field, date ? date.format('YYYY-MM-DD') : '')}
        style={
          wrapForTableHeader
            ? { width: '100%', marginBottom: 8 }
            : { minWidth: 160, width: '100%', maxWidth: 240 }
        }
        allowClear
        getPopupContainer={() => document.body}
        dropdownStyle={{ zIndex: 1100 }}
      />
    );
    if (wrapForTableHeader) {
      return (
        <div role="presentation" onMouseDown={(e) => e.stopPropagation()}>
          {picker}
        </div>
      );
    }
    return picker;
  };

  const createDateInput = (field, placeholder) =>
    createHeaderDatePicker(field, placeholder, searchFilters, handleSearchChange);

  const createImportSearchInput = (field, placeholder) => (
    <div className="searchEquip">
      <Input
        placeholder={placeholder}
        value={importSearchFilters[field]}
        onChange={(e) => handleImportSearchChange(field, e.target.value)}
        allowClear
      />
    </div>
  );

  const createImportDateInput = (field, placeholder) =>
    createHeaderDatePicker(field, placeholder, importSearchFilters, handleImportSearchChange);

  const filteredImportHeatProfileTableRows = useMemo(() => {
    return importHeatProfileTableRows.filter((item) => {
      return Object.keys(importSearchFilters).every((key) => {
        if (!importSearchFilters[key]) return true;

        if (key === 'createdDate') {
          return rowMatchesCreatedDateFilter(item, importSearchFilters.createdDate);
        }

        const value = item[key];
        if (typeof value === 'string') {
          return value.toLowerCase().includes(importSearchFilters[key].toLowerCase());
        }
        if (value !== undefined && value !== null) {
          return String(value).toLowerCase().includes(importSearchFilters[key].toLowerCase());
        }
        return false;
      });
    });
  }, [importHeatProfileTableRows, importSearchFilters]);

  useEffect(() => {
    setImportTablePagination((p) => ({ ...p, current: 1 }));
  }, [
    importSearchFilters.reportNumber,
    importSearchFilters.projectName,
    importSearchFilters.projectNumber,
    importSearchFilters.createdDate,
  ]);

  useEffect(() => {
    const total = filteredImportHeatProfileTableRows.length;
    const { current, pageSize } = importTablePagination;
    if (total === 0) return;
    const lastPage = Math.max(1, Math.ceil(total / pageSize));
    if (current > lastPage) {
      setImportTablePagination((p) => ({ ...p, current: lastPage }));
    }
  }, [filteredImportHeatProfileTableRows.length, importTablePagination.current, importTablePagination.pageSize]);

  const importHeatProfileColumns = [
    {
      title: (
        <div>
          <div style={{ color: '#ff4d4f', fontWeight: 'bold', marginBottom: 8 }}>Report Number</div>
          {createImportSearchInput('reportNumber', 'Search')}
        </div>
      ),
      dataIndex: 'reportNumber',
      key: 'reportNumber',
      width: 200,
      ellipsis: true,
      render: (text) => <Text style={{ color: '#fff' }}>{text || 'N/A'}</Text>,
    },
    {
      title: (
        <div>
          <div style={{ color: '#ff4d4f', fontWeight: 'bold', marginBottom: 8 }}>Project Name</div>
          {createImportSearchInput('projectName', 'Search')}
        </div>
      ),
      dataIndex: 'projectName',
      key: 'projectName',
      width: 240,
      ellipsis: true,
      render: (text) => <Text style={{ color: '#fff' }}>{text || 'N/A'}</Text>,
    },
    {
      title: (
        <div>
          <div style={{ color: '#ff4d4f', fontWeight: 'bold', marginBottom: 8 }}>Project Number</div>
          {createImportSearchInput('projectNumber', 'Search')}
        </div>
      ),
      dataIndex: 'projectNumber',
      key: 'projectNumber',
      width: 180,
      ellipsis: true,
      render: (text) => <Text style={{ color: '#fff' }}>{text || 'N/A'}</Text>,
    },
    {
      title: (
        <div>
          <div style={{ color: '#ff4d4f', fontWeight: 'bold', marginBottom: 8 }}>Created Date</div>
          {createImportDateInput('createdDate', 'Select date')}
        </div>
      ),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      align: 'center',
      render: (_, record) => (
        <Text style={{ color: '#fff' }}>
          {formatTableDateDisplay(getRowCreatedAtForFilter(record))}
        </Text>
      ),
    },
  ];

  // Table columns with search filters
  const columns = [
    {
      title: (
        <div>
          <div style={{ color: '#ff4d4f', fontWeight: 'bold', marginBottom: 8 }}>Report Number</div>
          {createSearchInput('reportNumber', 'Search')}
        </div>
      ),
      dataIndex: 'reportNumber',
      key: 'reportNumber',
      width: 180,
      render: (text) => (
        <Text strong style={{ color: 'white' }}>
          {text || 'N/A'}
        </Text>
      ),
    },
    {
      title: (
        <div>
          <div style={{ color: '#ff4d4f', fontWeight: 'bold', marginBottom: 8 }}>Project Name</div>
          {createSearchInput('projectName', 'Search')}
        </div>
      ),
      dataIndex: 'projectName',
      key: 'projectName',
      width: 200,
      render: (text) => text || 'N/A',
    },
    {
      title: (
        <div>
          <div style={{ color: '#ff4d4f', fontWeight: 'bold', marginBottom: 8 }}>
            Project Number
          </div>
          {createSearchInput('projectNumber', 'Search')}
        </div>
      ),
      dataIndex: 'projectNumber',
      key: 'projectNumber',
      width: 150,
      render: (text) => text || 'N/A',
    },
    {
      title: (
        <div>
          <div style={{ color: '#ff4d4f', fontWeight: 'bold', marginBottom: 8 }}>Report Author</div>
          {createSearchInput('reportAuthor', 'Search')}
        </div>
      ),
      dataIndex: 'reportAuthor',
      key: 'reportAuthor',
      width: 150,
      render: (text) => text || 'N/A',
    },
    {
      title: (
        <div>
          <div style={{ color: '#ff4d4f', fontWeight: 'bold', marginBottom: 8 }}>Material</div>
          {createSearchInput('material', 'Search')}
        </div>
      ),
      dataIndex: 'material',
      key: 'material',
      width: 180,
      render: (text) => text || '—',
    },
    {
      title: (
        <div>
          <div style={{ color: '#ff4d4f', fontWeight: 'bold', marginBottom: 8 }}>Trial Date</div>
          {createDateInput('trialDate', 'Select date')}
        </div>
      ),
      dataIndex: 'trailDate',
      key: 'trailDate',
      width: 120,
      render: (text) => formatTableDateDisplay(text),
    },
    {
      title: (
        <div>
          <div style={{ color: '#ff4d4f', fontWeight: 'bold', marginBottom: 8 }}>Created Date</div>
        </div>
      ),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (_, record) => formatTableDateDisplay(getRowCreatedAtForFilter(record)),
    },
    {
      title: <div style={{ color: '#ff4d4f', fontWeight: 'bold' }}>Files</div>,
      key: 'files',
      width: 100,
      render: (_, record) => (
        <Space>
          {record.pdfReportDetails?.s3Url ? (
            <Tooltip title="View PDF">
              <FilePdfOutlined
                style={{
                  color: '#ff4d4f',
                  fontSize: '16px',
                  cursor: 'pointer',
                }}
                onClick={() => handleView(record)}
              />
            </Tooltip>
          ) : (
            <Tooltip title="PDF not available">
              <FilePdfOutlined
                style={{
                  color: '#ff4d4f',
                  fontSize: '16px',
                  cursor: 'not-allowed',
                }}
              />
            </Tooltip>
          )}
          {record.textFileDetails?.s3Url ? (
            <Tooltip title="View Document">
              <FileTextOutlined
                style={{
                  color: '#ff4d4f',
                  fontSize: '16px',
                  cursor: 'pointer',
                }}
                onClick={() => handleViewText(record)}
              />
            </Tooltip>
          ) : (
            <Tooltip title="Document not available">
              <FileTextOutlined
                style={{
                  color: '#ff4d4f',
                  fontSize: '16px',
                  cursor: 'not-allowed',
                }}
              />
            </Tooltip>
          )}
          {record.imageFileDetails?.s3Url ? (
            <Tooltip title="View Images">
              <FileImageOutlined
                style={{
                  color: '#ff4d4f',
                  fontSize: '16px',
                  cursor: 'pointer',
                }}
                onClick={() => handleViewImage(record)}
              />
            </Tooltip>
          ) : (
            <Tooltip title="Images not available">
              <FileImageOutlined
                style={{
                  color: '#ff4d4f',
                  fontSize: '16px',
                  cursor: 'not-allowed',
                }}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: <div style={{ color: '#ff4d4f', fontWeight: 'bold' }}>Actions</div>,
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              loading={isEditLoading}
              style={{ color: '#ff4d4f' }}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="link"
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => showDeleteModal(record)}
              style={{ color: '#ff4d4f' }}
            />
          </Tooltip>
          <Tooltip title="Share">
            <Button
              type="link"
              size="small"
              icon={<ShareAltOutlined />}
              onClick={() => handleShare(record)}
              style={{ color: '#ff4d4f' }}
            />
          </Tooltip>
          <Tooltip title="Copy">
            <Button
              type="link"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => handleCopy(record)}
              style={{ color: '#ff4d4f' }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div
      style={{
        background: '#1a1a1a',
        minHeight: '100vh',
        padding: '0px',
        color: 'white',
      }}
    >
      {/* Header */}
      <Header
        style={{
          background: '#2d2d2d',
          padding: '0 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #404040',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Button
            icon={
              <ArrowLeftOutlined
                style={{ color: '#ff4d4f', fontSize: '18px', marginRight: '12px' }}
              />
            }
            onClick={onBack}
            style={{
              borderColor: 'transparent',
            }}
          />

          <Title level={4} style={{ color: 'white', margin: 0 }}>
            List of Heat profile
          </Title>
        </div>

        <div className="new-calibration">
          {/* <Button
            onClick={() => {
              console.log('Create Heat Profile button clicked in styled component');
              console.log('onCreateNew function:', onCreateNew);
              if (typeof onCreateNew === 'function') {
                onCreateNew();
              } else {
                console.error('onCreateNew is not a function:', onCreateNew);
              }
            }}
            className="ps-3 add-new-calibration"
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', fontWeight: 400 }}
          >
            <span
              className="icon-22"
              style={{ display: 'inline-flex', alignItems: 'center', marginRight: 6 }}
            >
              <svg>
                <use xlinkHref="#add" />
              </svg>
            </span>
            Create Heat Profile
          </Button> */}
          <Button
            onClick={handleOpenImportHeatProfileModal}
            className="ps-3 add-new-calibration"
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', fontWeight: 400 }}
          >
            <span
              className="icon-22"
              style={{ display: 'inline-flex', alignItems: 'center', marginRight: 6 }}
            >
              <UploadOutlined />
            </span>
            Import Heat Profile
          </Button>
        </div>
      </Header>

      {/* Content */}

      <Content style={{ padding: '12px', background: '#1a1a1a' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 12,
            marginBottom: 12,
          }}
        >
          <Text style={{ color: 'rgba(255,255,255,0.85)' }}>Filter by created date</Text>
          {createHeaderDatePicker(
            'createdDate',
            'Select date',
            searchFilters,
            handleSearchChange,
            { wrapForTableHeader: false }
          )}
        </div>
        <Table
          dataSource={filteredData}
          columns={columns}
          loading={isLoadingData}
          rowKey="id"
          size="small"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          }}
          style={{
            background: '#1a1a1a',
            '.ant-table': {
              background: '#1a1a1a',
            },
            '.ant-table-thead > tr > th': {
              background: '#2d2d2d',
              borderBottom: '1px solid #404040',
              color: '#ff4d4f',
            },
            '.ant-table-tbody > tr > td': {
              background: '#1a1a1a',
              borderBottom: '1px solid #404040',
              color: 'white',
            },
            '.ant-table-tbody > tr:hover > td': {
              background: '#2d2d2d',
            },
          }}
        />
      </Content>

      {/* Email Report Modal */}
      <Modal
        title="Send Heat Profile Report"
        open={isReportModalVisible}
        onCancel={() => {
          setIsReportModalVisible(false);
          setSelectedRecord(null);
        }}
        footer={null}
        width={600}
      >
        <EmailReportForm
          record={selectedRecord}
          onSend={handleSendReport}
          onCancel={() => {
            setIsReportModalVisible(false);
            setSelectedRecord(null);
          }}
        />
      </Modal>

      {/* Import: technical list from REACT_APP_AWS_CRCE_TECHNICAL_HEAT_PROFILE */}
      <Modal
        title={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 16,
              width: '100%',
              paddingRight: 8,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Button
                icon={
                  <ArrowLeftOutlined
                    style={{ color: '#ff4d4f', fontSize: '18px', marginRight: '12px' }}
                  />
                }
                onClick={handleCloseImportHeatProfileModal}
                style={{
                  borderColor: 'transparent',
                }}
              />
              <Title level={4} style={{ color: 'white', margin: 0 }}>
                Import Heat Profile
              </Title>
            </div>
            <div className="new-calibration">
              <Button
                className="ps-3 add-new-calibration"
                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', fontWeight: 400 }}
                loading={importBulkSubmitLoading}
                onClick={handleAddHeatProfileFromImportModal}
              >
                <span
                  className="icon-22"
                  style={{ display: 'inline-flex', alignItems: 'center', marginRight: 6 }}
                >
                  <PlusOutlined />
                </span>
                Add Heat Profile
              </Button>
            </div>
          </div>
        }
        open={isImportHeatProfileModalVisible}
        onCancel={handleCloseImportHeatProfileModal}
        footer={null}
        closeIcon={null}
        width="min(1320px, 98vw)"
        className="pipeline-modal"
        wrapClassName="import-heat-profile-modal"
        destroyOnClose
        bodyStyle={{ background: '#1a1a1a', paddingTop: 16 }}
      >
        <ConfigProvider
          getPopupContainer={(triggerNode) =>
            triggerNode?.closest?.('.ant-modal') || document.body
          }
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 12,
              marginBottom: 12,
            }}
          >
            <Text style={{ color: 'rgba(255,255,255,0.85)' }}>Filter by created date</Text>
            {createHeaderDatePicker(
              'createdDate',
              'Select date',
              importSearchFilters,
              handleImportSearchChange,
              { wrapForTableHeader: false }
            )}
          </div>
          <Table
            rowKey={(row) => getImportHeatProfileRowKey(row)}
            columns={importHeatProfileColumns}
            dataSource={filteredImportHeatProfileTableRows}
            loading={crceTechnicalImportListLoading}
            rowSelection={{
              selectedRowKeys: selectedImportRowKeys,
              onChange: (keys) => setSelectedImportRowKeys(keys),
              preserveSelectedRowKeys: true,
            }}
            pagination={{
              current: importTablePagination.current,
              pageSize: importTablePagination.pageSize,
              total: filteredImportHeatProfileTableRows.length,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              onChange: (page, pageSize) => {
                setImportTablePagination((prev) => ({
                  current: page,
                  pageSize: pageSize !== undefined ? pageSize : prev.pageSize,
                }));
              },
              onShowSizeChange: (_page, size) => {
                setImportTablePagination({ current: 1, pageSize: size });
              },
            }}
            size="small"
            scroll={{ x: 900 }}
            className="h-100 pipeline-table import-heat-profile-import-table"
            style={{
              background: '#1a1a1a',
              '.ant-table': {
                background: '#1a1a1a',
              },
              '.ant-table-thead > tr > th': {
                background: '#2d2d2d',
                borderBottom: '1px solid #404040',
                color: '#ff4d4f',
              },
              '.ant-table-tbody > tr > td': {
                background: '#1a1a1a',
                borderBottom: '1px solid #404040',
                color: 'white',
              },
              '.ant-table-tbody > tr:hover > td': {
                background: '#2d2d2d',
              },
            }}
          />
        </ConfigProvider>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Delete Project Heat Profile"
        open={isDeleteModalVisible}
        onOk={handleDelete}
        onCancel={cancelDelete}
        confirmLoading={isDeleteLoading}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
      >
        <p>Are you sure you want to delete this Project Heat Profile?</p>
        {selectedRecord && (
          <p>
            <Text strong>Report Number:</Text>{' '}
            {typeof selectedRecord.reportNumber === 'string' ? selectedRecord.reportNumber : 'N/A'}
          </p>
        )}
      </Modal>
    </div>
  );
};

export default ProjectHeatProfileList;
